/**
 * Where the eleven stand.
 *
 * Two separate things live here, and the original keeps them separate too:
 *
 *   1. A team's FORMATION — eleven slots in an abstract square running -1..1 in both
 *      directions, where -1 in x is your own goal and ±1 in y are the two touchlines.
 *      Built once when the team is loaded (`TeamData::TeamData`, teamdata.cpp:100-170).
 *   2. Turning those slots into metres on the pitch for a particular restart. Only the
 *      kick-off is implemented here; the other six restarts run through a much larger
 *      function that also reacts to the ball (docs/original-engine/match-ai.md §4.9).
 *
 * Both are pure functions of their inputs, so they are testable without a match.
 */

import { Vector3 } from 'three';
import { PITCH } from '../core/constants';
import { clamp } from '../core/math';

export type PlayerRole = 'GK' | 'CB' | 'LB' | 'RB' | 'DM' | 'CM' | 'LM' | 'RM' | 'AM' | 'CF';

export interface FormationEntry {
  role: PlayerRole;
  /** As authored in the team's data, in the -1..1 square. */
  databasePosition: Vector3;
  /** The one actually used: the authored slot pulled towards the role's own anchor. */
  position: Vector3;
}

/**
 * Where each role "wants" to stand, regardless of what a team's formation says
 * (`GetDefaultRolePosition`, teamdata.cpp:15-57).
 *
 * Note that a centre back and a keeper share an anchor. That is the original's, not a slip:
 * keepers are excluded from the separation pass below, so the two never push each other.
 */
export const ROLE_ANCHOR: Record<PlayerRole, [number, number]> = {
  GK: [-1.0, 0.0],
  CB: [-1.0, 0.0],
  LB: [-0.8, 0.8],
  RB: [-0.8, -0.8],
  DM: [-0.5, 0.0],
  CM: [0.0, 0.0],
  LM: [0.0, 1.0],
  RM: [0.0, -1.0],
  AM: [0.5, 0.0],
  CF: [1.0, 0.0],
};

/**
 * How far forward each role plays, 0..1. Used all over the team AI as a single knob
 * (`AIfunctions.cpp:1278-1299`). Not used yet; recorded here so it lives with the roles.
 */
export const ROLE_OFFENSIVENESS: Record<PlayerRole, number> = {
  GK: 0.0,
  CB: 0.0,
  LB: 0.25,
  RB: 0.25,
  DM: 0.25,
  LM: 0.5,
  CM: 0.5,
  RM: 0.5,
  AM: 0.75,
  CF: 1.0,
};

const MIN_SEPARATION = 0.5; // teamdata.cpp:128 — in the -1..1 square, so the range is 2
const MAX_RELAX_ITERATIONS = 10;

/**
 * Build a usable formation from the eleven authored slots.
 *
 * Step one blends each slot 60/40 towards its role's anchor, which is the original's way of
 * stopping a user-edited formation from becoming absurd (teamdata.cpp:116-117).
 *
 * Step two pushes slots apart until nobody is standing on anybody, up to ten passes
 * (teamdata.cpp:127-167). Keepers sit the pass out entirely. Two details are easy to get
 * wrong and are deliberate here: displacements for a whole pass are accumulated first and
 * applied together, and a pair that lands in exactly the same spot is separated along the
 * touchline direction rather than at random.
 */
export function buildFormation(
  slots: { role: PlayerRole; position: [number, number] }[],
): FormationEntry[] {
  const entries: FormationEntry[] = slots.map((slot) => {
    const anchor = ROLE_ANCHOR[slot.role];
    return {
      role: slot.role,
      databasePosition: new Vector3(slot.position[0], slot.position[1], 0),
      position: new Vector3(
        slot.position[0] * 0.6 + anchor[0] * 0.4,
        slot.position[1] * 0.6 + anchor[1] * 0.4,
        0,
      ),
    };
  });

  const diff = new Vector3();
  const push = new Vector3();

  for (let pass = 0; pass < MAX_RELAX_ITERATIONS; pass++) {
    const offsets = entries.map(() => new Vector3());
    let changed = false;

    for (let a = 0; a < entries.length - 1; a++) {
      if (entries[a]!.role === 'GK') continue;
      for (let b = a + 1; b < entries.length; b++) {
        if (entries[b]!.role === 'GK') continue;

        diff.subVectors(entries[a]!.position, entries[b]!.position);
        const distance = diff.length();
        if (distance >= MIN_SEPARATION) continue;

        changed = true;
        const closeness = 1 - distance / MIN_SEPARATION;
        // Coincident slots have no direction to separate along; the original falls back to
        // (0, 1, 0) — straight towards a touchline (`GetNormalized(Vector3(0, 1, 0))`).
        if (distance > 0) push.copy(diff).divideScalar(distance);
        else push.set(0, 1, 0);
        push.multiplyScalar(MIN_SEPARATION * closeness * 0.5);

        offsets[a]!.add(push);
        offsets[b]!.sub(push);
      }
    }

    if (!changed) break;

    entries.forEach((entry, i) => {
      entry.position.add(offsets[i]!);
      entry.position.x = clamp(entry.position.x, -1, 1);
      entry.position.y = clamp(entry.position.y, -1, 1);
    });
  }

  return entries;
}

// ---------------------------------------------------------------------------
// Kick-off
// ---------------------------------------------------------------------------

/** -1 for the team defending the negative-x goal, +1 for the other. `Team::GetSide`. */
export type Side = -1 | 1;

export interface PlacedPlayer {
  /** Index into the eleven. */
  slot: number;
  role: PlayerRole;
  /** Metres, in the simulation's Z-up frame. */
  position: Vector3;
}

/** The keeper's kick-off spot: on his line, near enough to it to be on it. */
export function keeperKickOffPosition(side: Side): Vector3 {
  return new Vector3(PITCH.halfLength * side * 0.98, 0, 0);
}

/**
 * Place one team for a kick-off (`TeamAIController::PrepareSetPiece`,
 * teamAIcontroller.cpp:698-720).
 *
 * The formation square is stretched over 60% of the pitch, then squashed to half depth and
 * shifted back into the team's own half, because at a kick-off nobody may cross the halfway
 * line. Two hard rules follow: stay on your own side, and stay out of the centre circle.
 *
 * `jitter` supplies the ±2 m sideways scatter the original adds "to stop people from bumping
 * into each other"; pass a seeded generator when you want the same line-up twice.
 */
export function placeKickOff(
  formation: FormationEntry[],
  side: Side,
  options: { isTaker: boolean; jitter?: () => number } = { isTaker: false },
): PlacedPlayer[] {
  const random = options.jitter ?? Math.random;
  const placed: PlacedPlayer[] = [];

  formation.forEach((entry, slot) => {
    if (entry.role === 'GK') {
      placed.push({ slot, role: entry.role, position: keeperKickOffPosition(side) });
      return;
    }

    const position = new Vector3(
      entry.position.x * -side * PITCH.halfLength * 0.6,
      entry.position.y * -side * PITCH.halfWidth * 0.6,
      0,
    );

    position.y += -2 + random() * 4;
    position.x *= 0.5;
    position.x += PITCH.halfLength * 0.2 * side;

    // Own half only.
    if (position.x * side < 0.5) position.x = 0.5 * side;

    // Out of the centre circle. The fallback direction matters when a player's slot lands
    // exactly on the centre spot: the original normalises towards his own goal.
    if (position.length() < 9.4) {
      if (position.length() > 0) position.normalize();
      else position.set(side, 0, 0);
      position.multiplyScalar(9.4);
    }

    placed.push({ slot, role: entry.role, position });
  });

  if (options.isTaker) placeKickOffTakers(placed, side);

  return placed;
}

/**
 * The two players who actually start the match: one on the centre spot, one 1.4 m to the
 * side of it.
 *
 * FAITHFULNESS NOTE. In the original this runs INSIDE the loop that places the ten outfield
 * players, so it fires ten times, each time re-picking the two players nearest the centre
 * spot (teamAIcontroller.cpp:712-718). Running it once at the end, as here, gives the same
 * answer whenever every player has already been placed by the formula — which is the case
 * at the start of a match. It can differ mid-match, because the original's first pass sees
 * nine players still standing wherever they were before the whistle. Reproducing that needs
 * the previous positions, which do not exist yet; when they do, this should move back inside
 * the loop.
 */
function placeKickOffTakers(placed: PlacedPlayer[], side: Side): void {
  const outfield = placed.filter((p) => p.role !== 'GK');
  const byDistance = [...outfield].sort((a, b) => a.position.length() - b.position.length());
  byDistance.slice(0, 2).forEach((player, i) => {
    player.position.set(0, i * 1.4 * side, 0);
  });
}

/**
 * Which way a player looks when he is put somewhere: at the ball, or straight down the
 * negative-y axis if he is standing on it (`HumanoidBase::ResetPosition`,
 * humanoidbase.cpp:929-947).
 *
 * Returns radians, measured from the original's rest facing of (0, -1, 0).
 */
export function facingTowards(from: Vector3, target: Vector3): number {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  if (dx === 0 && dy === 0) return 0;
  return Math.atan2(dx, -dy);
}
